import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../assets/propertyViewingsReport.module.css";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const PropertyViewingsReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    user_id: "",
    date: "",
    date_to: "",
    viewingsCount: "",
    viewingsCount_operator: ">=",
    viewingsTaken: "",
    viewingsTaken_operator: ">=",
    viewingsAttendees: "",
    viewingsAttendees_operator: ">=",
  });

  const [tableControls, setTableControls] = useState({
    entriesPerPage: 30,
    searchTerm: "",
    showColumnVisibility: false,
  });

  const [formFilteredRecords, setFormFilteredRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // State for sorting
  const [sortField, setSortField] = useState("user");
  const [sortDirection, setSortDirection] = useState("asc");

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    user: true,
    property: true,
    id: true,
    postcode: true,
    viewings: true,
    attendees: true,
    vacant: true,
    taken: true,
    reduction: true,
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle search term filtering and sorting - apply on top of form filtered records
  useEffect(() => {
    let filtered = [...formFilteredRecords]; // Create a copy to avoid mutating the original array
    
    // Apply search filter
    if (tableControls.searchTerm) {
      filtered = filtered.filter((record) =>
        Object.values(record).some(
          (value) =>
            value &&
            value
              .toString()
              .toLowerCase()
              .includes(tableControls.searchTerm.toLowerCase())
        )
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "user") {
        aValue = a.fullName || "";
        bValue = b.fullName || "";
      } else if (sortField === "property") {
        aValue = a.propertyName || "";
        bValue = b.propertyName || "";
      } else if (sortField === "id") {
        aValue = a.property_id || "";
        bValue = b.property_id || "";
      } else if (sortField === "postcode") {
        aValue = a.postcode || "";
        bValue = b.postcode || "";
      } else if (sortField === "viewings") {
        aValue = a.viewingsCount || 0;
        bValue = b.viewingsCount || 0;
      } else if (sortField === "attendees") {
        aValue = a.viewingsAttendees || 0;
        bValue = b.viewingsAttendees || 0;
      } else if (sortField === "vacant") {
        aValue = typeof a.vacant === 'number' ? a.vacant : 0;
        bValue = typeof b.vacant === 'number' ? b.vacant : 0;
      } else if (sortField === "taken") {
        aValue = a.viewingsTaken || 0;
        bValue = b.viewingsTaken || 0;
      } else if (sortField === "reduction") {
        // Handle percentage strings like "66.67%" or "-"
        aValue = typeof a.reduction === 'string' && a.reduction !== '-' ? 
          parseFloat(a.reduction.replace('%', '')) : 0;
        bValue = typeof b.reduction === 'string' && b.reduction !== '-' ? 
          parseFloat(b.reduction.replace('%', '')) : 0;
      }

      // Handle numeric sorting
      if (sortField === "viewings" || sortField === "attendees" || sortField === "vacant" || sortField === "taken" || sortField === "reduction") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    setFilteredRecords(filtered);
    setCurrentPage(1); // Reset to first page when search or sort changes
  }, [tableControls.searchTerm, formFilteredRecords, sortField, sortDirection]);

  // Handle entries per page change
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when entries per page changes
  }, [tableControls.entriesPerPage]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };


  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load users for the dropdown
      console.log("🔍 Fetching users from:", "/api/propertyviewings/report");
      const usersResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/propertyviewings/report`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        console.log("✅ Users data received:", usersData);
        if (usersData.success && usersData.data.users) {
          // Convert users object to array format expected by component
          const usersArray = Object.entries(usersData.data.users)
            .map(([id, name]) => ({
              id: id === "All" ? "" : id,
              full_name: name,
            }))
            .filter((user) => user.id !== ""); // Remove 'All' option since we handle it separately
          console.log("👥 Users converted:", usersArray);
          setUsers(usersArray);
        }
      } else {
        console.error("Users API failed:", usersResponse.status, usersResponse.statusText);
        toast.error("Failed to load users. Please try again later.");
        setUsers([]);
      }

      // Load initial report data
      await loadReportData();
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast.error("Failed to load report data. Please try again later.");
      setUsers([]);
      setRecords([]);
      setFormFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadReportData = async () => {
    try {
      console.log("📊 Loading report data with filters:", formData);
      const queryParams = new URLSearchParams();

      if (formData.user_id) queryParams.append("userId", formData.user_id);
      if (formData.date) queryParams.append("date", formData.date);
      if (formData.date_to) queryParams.append("dateTo", formData.date_to);

      // Add required parameters for DataTables format
      queryParams.append("draw", "1");
      queryParams.append("start", "0");
      queryParams.append("length", "1000"); // Get all records
      if (formData.viewingsCount) {
        queryParams.append("viewingsCount", formData.viewingsCount);
        queryParams.append(
          "viewingsCount_operator",
          formData.viewingsCount_operator
        );
      }
      if (formData.viewingsTaken) {
        queryParams.append("viewingsTaken", formData.viewingsTaken);
        queryParams.append(
          "viewingsTaken_operator",
          formData.viewingsTaken_operator
        );
      }
      if (formData.viewingsAttendees) {
        queryParams.append("viewingsAttendees", formData.viewingsAttendees);
        queryParams.append(
          "viewingsAttendees_operator",
          formData.viewingsAttendees_operator
        );
      }

      console.log(
        "🔍 Fetching report data from:",
        `/api/propertyviewings/report/data?${queryParams.toString()}`
      );
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }/api/propertyviewings/report/data?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Report data received:", data);
        // Backend returns data in DataTables format: { data: [...], recordsTotal: X }
        const recordsData = data.data || [];
        console.log("📊 Records count:", recordsData.length);
        console.log("📝 Sample record structure:", recordsData[0]);
        
        // Log property IDs to verify navigation will work
        if (recordsData.length > 0) {
          console.log("🔍 Property IDs in data:", recordsData.map(r => ({ 
            name: r.propertyName, 
            propertyId: r.propertyId, 
            property_id: r.property_id 
          })));
        }
        
        setRecords(recordsData);
        setFormFilteredRecords(recordsData);
        setCurrentPage(1);
      } else {
        const errorText = await response.text();
        console.error("API failed:", response.status, response.statusText, errorText);
        toast.error(`Failed to load report data: ${response.statusText}. Please try again later.`);
        setRecords([]);
        setFormFilteredRecords([]);
      }
    } catch (error) {
      console.error("Error loading report data:", error);
      toast.error(`Failed to load report data: ${error.message}. Please try again later.`);
      setRecords([]);
      setFormFilteredRecords([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🔍 Search button clicked with filters:", formData);
    setLoading(true);
    try {
      await loadReportData();
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = async () => {
    console.log("🗑️ Clearing all filters");
    const clearedFormData = {
      user_id: "",
      date: "",
      date_to: "",
      viewingsCount: "",
      viewingsCount_operator: ">=",
      viewingsTaken: "",
      viewingsTaken_operator: ">=",
      viewingsAttendees: "",
      viewingsAttendees_operator: ">=",
    };
    setFormData(clearedFormData);
    
    // Reload data from API with cleared filters
    setLoading(true);
    try {
      // Call loadReportData with cleared filters directly
      const queryParams = new URLSearchParams();
      queryParams.append("draw", "1");
      queryParams.append("start", "0");
      queryParams.append("length", "1000");

      console.log("🔍 Fetching report data with cleared filters");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/propertyviewings/report/data?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const recordsData = data.data || [];
        setRecords(recordsData);
        setFormFilteredRecords(recordsData);
        setCurrentPage(1);
      } else {
        const errorText = await response.text();
        console.error("API failed:", response.status, response.statusText, errorText);
        toast.error(`Failed to load report data: ${response.statusText}. Please try again later.`);
        setRecords([]);
        setFormFilteredRecords([]);
      }
    } catch (error) {
      console.error("Error reloading data after clearing filters:", error);
      toast.error("Failed to reload data. Please try again.");
      setRecords([]);
      setFormFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters = () => {
    return (
      formData.user_id ||
      formData.date ||
      formData.date_to ||
      formData.viewingsCount ||
      formData.viewingsTaken ||
      formData.viewingsAttendees
    );
  };

  // Helper function to parse property HTML from backend
  const parsePropertyLink = (propertyName) => {
    if (typeof propertyName === "string" && propertyName.includes("<a href=")) {
      // Extract ID and name from HTML like: <a href="/properties/1">Red House</a>
      const hrefMatch = propertyName.match(/href="\/properties\/(\d+)"/);
      const nameMatch = propertyName.match(/>([^<]+)</);

      if (hrefMatch && nameMatch) {
        return {
          id: hrefMatch[1],
          name: nameMatch[1],
        };
      }
    }

    // Fallback for non-HTML strings
    return {
      id: null,
      name: propertyName,
    };
  };

  const handleTableControlChange = (e) => {
    const { name, value } = e.target;
    setTableControls((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleExportCSV = () => {
    try {
      // Get current filtered data
      const dataToExport = filteredRecords;

      // Build headers based on visible columns
      const headers = [];
      const columnKeys = [];

      if (visibleColumns.user) {
        headers.push("User");
        columnKeys.push("user");
      }
      if (visibleColumns.property) {
        headers.push("Property");
        columnKeys.push("property");
      }
      if (visibleColumns.id) {
        headers.push("ID");
        columnKeys.push("id");
      }
      if (visibleColumns.postcode) {
        headers.push("Postcode");
        columnKeys.push("postcode");
      }
      if (visibleColumns.viewings) {
        headers.push("Viewings");
        columnKeys.push("viewings");
      }
      if (visibleColumns.attendees) {
        headers.push("Attendees");
        columnKeys.push("attendees");
      }
      if (visibleColumns.vacant) {
        headers.push("Vacant");
        columnKeys.push("vacant");
      }
      if (visibleColumns.taken) {
        headers.push("Taken");
        columnKeys.push("taken");
      }
      if (visibleColumns.reduction) {
        headers.push("Reduction");
        columnKeys.push("reduction");
      }

      const csvContent = [
        headers.join(","),
        ...dataToExport.map((record) => {
          const row = [];
          if (visibleColumns.user) row.push(`"${record.fullName || ""}"`);
          if (visibleColumns.property)
            row.push(`"${record.propertyName || ""}"`);
          if (visibleColumns.id) row.push(`"${record.propertyId || ""}"`);
          if (visibleColumns.postcode) row.push(`"${record.postcode || ""}"`);
          if (visibleColumns.viewings) row.push(record.viewingsCount || 0);
          if (visibleColumns.attendees) row.push(record.viewingsAttendees || 0);
          if (visibleColumns.vacant)
            row.push(`"${record.vacant || "need From field"}"`);
          if (visibleColumns.taken) row.push(record.viewingsTaken || 0);
          if (visibleColumns.reduction) {
            row.push(
              `"${
                record.vacant && record.viewingsTaken
                  ? calculateReduction(record.viewingsTaken, record.vacant)
                  : "-"
              }"`
            );
          }
          return row.join(",");
        }),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `property_viewings_report_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV file downloaded successfully!");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export for Property Viewings Report...");
      
      // Create PDF content using jsPDF (copying from ExpenseAccount.jsx)
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Property Viewings Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${filteredRecords.length}`, 20, 40);
      
      // Add table headers
      const headers = [];
      if (visibleColumns.user) headers.push('User');
      if (visibleColumns.property) headers.push('Property');
      if (visibleColumns.id) headers.push('ID');
      if (visibleColumns.postcode) headers.push('Postcode');
      if (visibleColumns.viewings) headers.push('Viewings');
      if (visibleColumns.attendees) headers.push('Attendees');
      if (visibleColumns.vacant) headers.push('Vacant');
      if (visibleColumns.taken) headers.push('Taken');
      if (visibleColumns.reduction) headers.push('Reduction');
      
      let yPosition = 60;
      const startX = 20;
      const colWidth = 25;
      
      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + (index * colWidth), yPosition);
      });
      
      yPosition += 10;
      
      // Draw data rows
      filteredRecords.forEach((record, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        
        const rowData = [];
        if (visibleColumns.user) rowData.push(String(record.fullName || '').substring(0, 20));
        if (visibleColumns.property) rowData.push(String(record.propertyName || '').substring(0, 20));
        if (visibleColumns.id) rowData.push(String(record.propertyId || '').substring(0, 20));
        if (visibleColumns.postcode) rowData.push(String(record.postcode || '').substring(0, 20));
        if (visibleColumns.viewings) rowData.push(String(record.viewingsCount || 0).substring(0, 20));
        if (visibleColumns.attendees) rowData.push(String(record.viewingsAttendees || 0).substring(0, 20));
        if (visibleColumns.vacant) rowData.push(String(record.vacant || 'need From field').substring(0, 20));
        if (visibleColumns.taken) rowData.push(String(record.viewingsTaken || 0).substring(0, 20));
        if (visibleColumns.reduction) {
          const reduction = record.vacant && record.viewingsTaken 
            ? calculateReduction(record.viewingsTaken, record.vacant)
            : '-';
          rowData.push(String(reduction).substring(0, 20));
        }
        
        rowData.forEach((cell, cellIndex) => {
          const safeCell = String(cell || '');
          doc.text(safeCell.substring(0, 20), startX + (cellIndex * colWidth), yPosition);
        });
        
        yPosition += 7;
      });
      
      // Save PDF
      doc.save(`property_viewings_report_${new Date().toISOString().split('T')[0]}.pdf`);
      
      console.log('PDF exported successfully!');
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error(`Failed to export PDF: ${error.message}`);
    }
  };

  const handleColumnVisibility = () => {
    setTableControls((prev) => ({
      ...prev,
      showColumnVisibility: !prev.showColumnVisibility,
    }));
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const calculateReduction = (taken, vacant) => {
    if (taken < 1) return "-";
    if (taken > 0 && vacant > 0) {
      return `${Math.round((taken / vacant) * 100 * 100) / 100}%`;
    }
    return "-";
  };

  // Calculate pagination
  const totalPages = Math.ceil(
    filteredRecords.length / tableControls.entriesPerPage
  );
  const startIndex = (currentPage - 1) * tableControls.entriesPerPage;
  const endIndex = startIndex + tableControls.entriesPerPage;
  const currentRecords = filteredRecords.slice(startIndex, endIndex);

  // Debug logging
  console.log("🔍 Current records for table:", currentRecords);
  console.log("📊 Filtered records count:", filteredRecords.length);
  if (currentRecords[0]) {
    console.log("📝 Sample record:", currentRecords[0]);
    console.log("🔑 property_id:", currentRecords[0].property_id);
    console.log("📮 postcode:", currentRecords[0].postcode);
    console.log("🆔 propertyId:", currentRecords[0].propertyId);
    console.log("🏠 propertyName:", currentRecords[0].propertyName);
    console.log("🏢 All record keys:", Object.keys(currentRecords[0]));
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          Loading property viewings report...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <span onClick={() => navigate("/dashboard")}>Dashboard</span> /
        <span onClick={() => navigate("/property_viewings")}>
          {" "}
          Property Viewings
        </span>{" "}
        /<span className={styles.breadcrumbCurrent}>Report</span>
      </div>

      <div className={styles.content}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>Property Viewing Report</h3>
          </div>
          <div className={styles.panelBody}>
            {/* Search Form */}
            <form onSubmit={handleSubmit} className={styles.searchForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="user_id">User</label>
                  <select
                    id="user_id"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleInputChange}
                    className={styles.formControl}
                  >
                    <option value="">All</option>
                    {Array.isArray(users) ? users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    )) : (
                      <option value="">No users available</option>
                    )}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="date">From</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className={styles.formControl}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="date_to">To</label>
                  <input
                    type="date"
                    id="date_to"
                    name="date_to"
                    value={formData.date_to}
                    onChange={handleInputChange}
                    className={styles.formControl}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="viewingsCount">Viewings</label>
                  <div className={styles.filterGroup}>
                    <select
                      name="viewingsCount_operator"
                      value={formData.viewingsCount_operator}
                      onChange={handleInputChange}
                      className={styles.operatorSelect}
                    >
                      <option value="=">=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                    </select>
                    <input
                      type="number"
                      name="viewingsCount"
                      value={formData.viewingsCount}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      placeholder="Count"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="viewingsTaken">Taken</label>
                  <div className={styles.filterGroup}>
                    <select
                      name="viewingsTaken_operator"
                      value={formData.viewingsTaken_operator}
                      onChange={handleInputChange}
                      className={styles.operatorSelect}
                    >
                      <option value="=">=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                    </select>
                    <input
                      type="number"
                      name="viewingsTaken"
                      value={formData.viewingsTaken}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      placeholder="Count"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="viewingsAttendees">Attendees</label>
                  <div className={styles.filterGroup}>
                    <select
                      name="viewingsAttendees_operator"
                      value={formData.viewingsAttendees_operator}
                      onChange={handleInputChange}
                      className={styles.operatorSelect}
                    >
                      <option value="=">=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                    </select>
                    <input
                      type="number"
                      name="viewingsAttendees"
                      value={formData.viewingsAttendees}
                      onChange={handleInputChange}
                      className={styles.formControl}
                      placeholder="Count"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={styles.btnSearch}
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Search"}
                </button>
                {hasActiveFilters() && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className={styles.btnClear}
                    disabled={loading}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </form>

            {/* Table Controls */}
            <div className={styles.tableControls}>
              <div className={styles.tableControlsLeft}>
                <span>Show</span>
                <select
                  name="entriesPerPage"
                  value={tableControls.entriesPerPage}
                  onChange={handleTableControlChange}
                  className={styles.entriesSelect}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>

              <div className={styles.tableControlsRight}>
                <div className={styles.searchControl}>
                  <label>Search:</label>
                  <input
                    type="text"
                    name="searchTerm"
                    value={tableControls.searchTerm}
                    onChange={handleTableControlChange}
                    className={styles.searchInput}
                    placeholder="Search..."
                  />
                </div>

                <div className={styles.actionButtons}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleExportCSV}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleExportPDF}
                  >
                    Export as PDF
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleColumnVisibility}
                  >
                    Column visibility
                  </button>

                  {/* Column Visibility Dropdown */}
                  {tableControls.showColumnVisibility && (
                    <div className={styles.columnVisibilityDropdown}>
                      <div className={styles.columnVisibilityHeader}>
                        <h4>Column Visibility</h4>
                        <button
                          type="button"
                          className={styles.closeBtn}
                          onClick={() =>
                            setTableControls((prev) => ({
                              ...prev,
                              showColumnVisibility: false,
                            }))
                          }
                        >
                          ×
                        </button>
                      </div>
                      <div className={styles.columnVisibilityContent}>
                        {Object.entries(visibleColumns).map(
                          ([column, isVisible]) => (
                            <label
                              key={column}
                              className={styles.columnVisibilityItem}
                            >
                              <input
                                type="checkbox"
                                checked={isVisible}
                                onChange={() => toggleColumnVisibility(column)}
                              />
                              <span className={styles.columnLabel}>
                                {column.charAt(0).toUpperCase() +
                                  column.slice(1)}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Results Table */}
            {currentRecords.length > 0 ? (
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      {visibleColumns.user && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("user")}
                          style={{ cursor: "pointer" }}
                        >
                          User<SortIcon direction={sortField === "user" ? sortDirection : null} isActive={sortField === "user"} />
                        </th>
                      )}
                      {visibleColumns.property && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("property")}
                          style={{ cursor: "pointer" }}
                        >
                          Property<SortIcon direction={sortField === "property" ? sortDirection : null} isActive={sortField === "property"} />
                        </th>
                      )}
                      {visibleColumns.id && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("id")}
                          style={{ cursor: "pointer" }}
                        >
                          ID<SortIcon direction={sortField === "id" ? sortDirection : null} isActive={sortField === "id"} />
                        </th>
                      )}
                      {visibleColumns.postcode && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("postcode")}
                          style={{ cursor: "pointer" }}
                        >
                          Postcode<SortIcon direction={sortField === "postcode" ? sortDirection : null} isActive={sortField === "postcode"} />
                        </th>
                      )}
                      {visibleColumns.viewings && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("viewings")}
                          style={{ cursor: "pointer" }}
                        >
                          Viewings<SortIcon direction={sortField === "viewings" ? sortDirection : null} isActive={sortField === "viewings"} />
                        </th>
                      )}
                      {visibleColumns.attendees && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("attendees")}
                          style={{ cursor: "pointer" }}
                        >
                          Attendees<SortIcon direction={sortField === "attendees" ? sortDirection : null} isActive={sortField === "attendees"} />
                        </th>
                      )}
                      {visibleColumns.vacant && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("vacant")}
                          style={{ cursor: "pointer" }}
                        >
                          Vacant<SortIcon direction={sortField === "vacant" ? sortDirection : null} isActive={sortField === "vacant"} />
                        </th>
                      )}
                      {visibleColumns.taken && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("taken")}
                          style={{ cursor: "pointer" }}
                        >
                          Taken<SortIcon direction={sortField === "taken" ? sortDirection : null} isActive={sortField === "taken"} />
                        </th>
                      )}
                      {visibleColumns.reduction && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("reduction")}
                          style={{ cursor: "pointer" }}
                        >
                          Reduction<SortIcon direction={sortField === "reduction" ? sortDirection : null} isActive={sortField === "reduction"} />
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.map((record, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 1 ? styles.evenRow : ""}
                      >
                        {visibleColumns.user && (
                          <td>{record.fullName || "N/A"}</td>
                        )}
                        {visibleColumns.property && (
                          <td>
                            {(() => {
                                                        const propertyInfo = parsePropertyLink(
                            record.propertyName
                          );
                          // Use direct propertyId from backend response
                          const propertyId = record.propertyId || propertyInfo.id;

                          return propertyId ? (
                            <span
                              onClick={() =>
                                navigate(`/properties/${propertyId}`)
                              }
                              className={styles.propertyLink}
                              style={{ cursor: "pointer", color: "#337ab7", textDecoration: "underline" }}
                            >
                              {propertyInfo.name || record.propertyName}
                            </span>
                          ) : (
                            propertyInfo.name || record.propertyName || "N/A"
                          );
                            })()}
                          </td>
                        )}
                        {visibleColumns.id && (
                          <td>
                            {record.property_id || record.propertyId || "N/A"}
                          </td>
                        )}
                        {visibleColumns.postcode && (
                          <td>{record.postcode || "N/A"}</td>
                        )}
                        {visibleColumns.viewings && (
                          <td>{record.viewingsCount || 0}</td>
                        )}
                        {visibleColumns.attendees && (
                          <td>{record.viewingsAttendees || 0}</td>
                        )}
                        {visibleColumns.vacant && (
                          <td>
                            {typeof record.vacant === "string" &&
                            record.vacant.includes("<b>") ? (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: record.vacant,
                                }}
                              />
                            ) : (
                              record.vacant || "need From field"
                            )}
                          </td>
                        )}
                        {visibleColumns.taken && (
                          <td>{record.viewingsTaken || 0}</td>
                        )}
                        {visibleColumns.reduction && (
                          <td>
                            {record.vacant && record.viewingsTaken
                              ? calculateReduction(
                                  record.viewingsTaken,
                                  record.vacant
                                )
                              : "-"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles.noData}>No data!</div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredRecords.length)} of{" "}
                  {filteredRecords.length.toLocaleString()} entries
                </div>
                <div className={styles.paginationControls}>
                  <button
                    type="button"
                    className={styles.paginationBtn}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>

                  {/* First page */}
                  <button
                    type="button"
                    className={`${styles.paginationBtn} ${
                      currentPage === 1 ? styles.activePage : ""
                    }`}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </button>

                  {/* Show ellipsis if there are many pages before current */}
                  {currentPage > 4 && (
                    <span className={styles.paginationEllipsis}>...</span>
                  )}

                  {/* Pages around current page */}
                  {(() => {
                    const pages = [];
                    const startPage = Math.max(2, currentPage - 2);
                    const endPage = Math.min(totalPages - 1, currentPage + 2);

                    for (
                      let pageNum = startPage;
                      pageNum <= endPage;
                      pageNum++
                    ) {
                      if (pageNum > 1 && pageNum < totalPages) {
                        pages.push(
                          <button
                            key={pageNum}
                            type="button"
                            className={`${styles.paginationBtn} ${
                              currentPage === pageNum ? styles.activePage : ""
                            }`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    }
                    return pages;
                  })()}

                  {/* Show ellipsis if there are many pages after current */}
                  {currentPage < totalPages - 3 && (
                    <span className={styles.paginationEllipsis}>...</span>
                  )}

                  {/* Last page */}
                  {totalPages > 1 && (
                    <button
                      type="button"
                      className={`${styles.paginationBtn} ${
                        currentPage === totalPages ? styles.activePage : ""
                      }`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}

                  <button
                    type="button"
                    className={styles.paginationBtn}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Report Description */}
            <div className={styles.reportDescription}>
              <p>
                These Report shows property viewings statistics grouped by user
                and property. It summarised only <strong>completed</strong>{" "}
                viewings.
              </p>
              <p>
                Vacant shows number of vacant room in property of the "From"
                date. That's the reason it doesn't display anything if "From"
                field is not filled. The system keeps information regarding
                daily numbers of vacant rooms of property since 21/02/2017.
              </p>
              <p>Reduction = Taken / Vacant * 100%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyViewingsReport;
